import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../hooks/useAuth'
import {
  RotateCcw,
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  RefreshCw,
  Eye,
  EyeOff,
  Settings,
  BarChart3,
  Clock,
  Target,
  Lightbulb,
  Volume2,
  Pause,
  Play
} from 'lucide-react'
import { RootState } from '../store'
import {
  fetchDeckById,
  startStudySession,
  updateCardProgress,
  completeStudySession
} from '../store/slices/flashcardSlice'
import { flashcardService } from '../services/flashcardService'
import { Flashcard, StudyMode, StudySessionResult } from '../types/flashcard'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ProgressBar from '../components/ui/ProgressBar'

interface FlashcardProps {
  card: Flashcard
  isFlipped: boolean
  onFlip: () => void
  showHint: boolean
  onToggleHint: () => void
}

const FlashcardComponent: React.FC<FlashcardProps> = ({
  card,
  isFlipped,
  onFlip,
  showHint,
  onToggleHint
}) => {
  const [isFlipping, setIsFlipping] = React.useState(false)
  
  const handleFlip = () => {
    if (isFlipping) return // Prevent multiple clicks during animation
    
    setIsFlipping(true)
    onFlip()
    
    // Reset flipping state after animation completes
    setTimeout(() => {
      setIsFlipping(false)
    }, 600)
  }
  
  return (
    <div className="relative w-full h-80 sm:h-96 perspective-1000">
      <div 
        className={`flashcard-container relative w-full h-full transform-style-preserve-3d cursor-pointer ${
          isFlipped ? 'rotate-y-180' : ''
        } ${
          isFlipping ? 'flipping' : ''
        }`}
        onClick={handleFlip}
      >
        {/* Front Side - Question */}
        <div className="flashcard-side backface-hidden">
          <Card className="w-full h-full p-4 sm:p-6 md:p-8 flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-200 hover:shadow-xl transition-shadow overflow-hidden">
            <div className="flex-1 flex flex-col justify-start items-center text-center min-h-0 space-y-3 sm:space-y-4">
              <div className="mb-2 sm:mb-4">
                <Badge className="bg-blue-100 text-blue-800 mb-2 text-xs sm:text-sm">
                  Soru
                </Badge>
              </div>
              
              <div className="text-base sm:text-lg md:text-xl font-medium text-gray-900 mb-4 sm:mb-6 leading-relaxed overflow-y-auto max-h-32 sm:max-h-40">
                {card.question || 'Soru yükleniyor...'}
              </div>
              
              {card.questionImage && (
                <div className="mb-4 sm:mb-6 flex-shrink-0">
                  <img 
                    src={card.questionImage} 
                    alt="Soru görseli" 
                    className="max-w-full max-h-20 sm:max-h-24 md:max-h-32 mx-auto rounded-lg shadow-md object-contain"
                  />
                </div>
              )}
              
              {card.hint && (
                <div className="mt-auto pt-2 sm:pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleHint()
                    }}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs sm:text-sm"
                  >
                    <Lightbulb size={14} className="mr-1 sm:mr-2" />
                    {showHint ? 'İpucunu Gizle' : 'İpucu Göster'}
                  </Button>
                  
                  {showHint && (
                    <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs sm:text-sm text-yellow-800">
                        💡 {card.hint}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-2 sm:mt-4 text-xs sm:text-sm text-gray-500 flex-shrink-0">
              Kartı çevirmek için tıklayın
            </div>
          </Card>
        </div>
        
        {/* Back Side - Answer */}
        <div className="flashcard-side backface-hidden">
          <Card className="w-full h-full p-4 sm:p-6 md:p-8 flex flex-col bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200 hover:shadow-xl transition-shadow overflow-hidden">
            <div className="flex-1 flex flex-col justify-start items-center text-center min-h-0 space-y-3 sm:space-y-4">
              <div className="flex-shrink-0">
                <Badge className="bg-green-100 text-green-800 text-xs sm:text-sm">
                  Cevap
                </Badge>
              </div>
              
              <div className="text-base sm:text-lg md:text-xl font-medium text-gray-900 leading-relaxed overflow-y-auto max-h-24 sm:max-h-32 flex-shrink-0">
                {card.answer || 'Cevap yükleniyor...'}
              </div>
              
              {card.answerImage && (
                <div className="flex-shrink-0">
                  <img 
                    src={card.answerImage} 
                    alt="Cevap görseli" 
                    className="max-w-full max-h-16 sm:max-h-20 md:max-h-24 mx-auto rounded-lg shadow-md object-contain"
                  />
                </div>
              )}
              
              {card.explanation && (
                <div className="flex-1 w-full max-h-32 sm:max-h-40 overflow-y-auto">
                  <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg text-left">
                    <h4 className="font-medium text-blue-900 mb-2 text-xs sm:text-sm">Açıklama:</h4>
                    <p className="text-xs sm:text-sm text-blue-800 leading-relaxed">
                      {card.explanation}
                    </p>
                  </div>
                </div>
              )}
              
              {card.tags && card.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 sm:gap-2 justify-center flex-shrink-0">
                  {card.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

const FlashcardStudy: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isAuthenticated, loading: authLoading } = useAuth()
  
  const {
    currentDeck,
    currentSession,
    loading,
    error
  } = useSelector((state: RootState) => state.flashcard)
  
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [studyMode, setStudyMode] = useState<StudyMode>({
    type: 'normal',
    showHints: true,
    autoFlip: false,
    timeLimit: null
  })
  const [sessionStats, setSessionStats] = useState({
    cardsStudied: 0,
    cardsCorrect: 0,
    cardsIncorrect: 0,
    cardsToReview: 0,
    startTime: new Date()
  })
  const [isSessionActive, setIsSessionActive] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  
  const currentCard = currentDeck?.cards[currentCardIndex]
  const totalCards = currentDeck?.cards.length || 0
  const progress = totalCards > 0 ? ((currentCardIndex + 1) / totalCards) * 100 : 0
  
  useEffect(() => {
    const fetchDeckWithErrorHandling = async () => {
      // Auth yüklenmesi tamamlanmadan işlem yapma
      if (authLoading) {
        console.log('FlashcardStudy - Auth still loading, waiting...')
        return
      }
      
      // Kullanıcı giriş yapmamışsa flashcards sayfasına yönlendir
      if (!isAuthenticated) {
        console.log('FlashcardStudy - User not authenticated, redirecting to flashcards')
        navigate('/flashcards')
        return
      }
      
      if (deckId) {
        console.log('FlashcardStudy - deckId from URL:', deckId)
        // Her zaman yeni deste yükle, cache'lenmiş deste farklı olabilir
        if (!currentDeck || currentDeck.id !== deckId) {
          try {
            await dispatch(fetchDeckById(deckId) as any)
          } catch (error) {
            console.error('🚨 Critical error in FlashcardStudy page:', error)
            
            // Check for Firestore internal errors
            if (error instanceof Error && (
              error.message.includes('INTERNAL ASSERTION FAILED') ||
              error.message.includes('Unexpected state') ||
              error.message.includes('client has already been terminated')
            )) {
              console.warn('🔄 Firestore internal error detected in study page, attempting recovery...')
              
              // Try to navigate back to flashcards page
              try {
                localStorage.clear()
                sessionStorage.clear()
                
                // Navigate back to flashcards page
                navigate('/flashcards')
                
                // Show user-friendly message
                setTimeout(() => {
                  window.location.reload()
                }, 1000)
              } catch (recoveryError) {
                console.error('Failed to recover from error:', recoveryError)
              }
            }
          }
        }
      }
    }
    
    fetchDeckWithErrorHandling()
  }, [deckId, dispatch, currentDeck, navigate, isAuthenticated, authLoading])
  
  useEffect(() => {
    if (currentDeck) {
      console.log('FlashcardStudy - currentDeck loaded:', {
        id: currentDeck.id,
        title: currentDeck.title,
        totalCards: currentDeck.cards.length,
        currentCard: currentDeck.cards[currentCardIndex]
      })
      
      // İlk kartı kontrol et
      if (currentDeck.cards[0]) {
        console.log('FlashcardStudy - First card:', {
          question: currentDeck.cards[0].question,
          answer: currentDeck.cards[0].answer,
          id: currentDeck.cards[0].id
        })
      }
    }
  }, [currentDeck, currentCardIndex])
  
  useEffect(() => {
    if (currentDeck && !isSessionActive) {
      handleStartSession()
    }
  }, [currentDeck, isSessionActive])
  
  // Reset card state when currentCardIndex changes
  useEffect(() => {
    setIsFlipped(false)
    setShowHint(false)
  }, [currentCardIndex])
  
  const handleStartSession = useCallback(async () => {
    if (!currentDeck) return
    
    try {
      await dispatch(startStudySession({ deckId: currentDeck.id, mode: studyMode }) as any)
      setIsSessionActive(true)
      setSessionStats({
        cardsStudied: 0,
        cardsCorrect: 0,
        cardsIncorrect: 0,
        cardsToReview: 0,
        startTime: new Date()
      })
    } catch (error) {
      console.error('Error starting session:', error)
    }
  }, [currentDeck, studyMode, dispatch])
  
  const handleCardResponse = useCallback(async (status: 'correct' | 'incorrect' | 'review') => {
    if (!currentCard || !currentDeck) return
    
    // Store current card reference before state changes
    const cardToProcess = currentCard
    
    // Immediately update UI state for better responsiveness
     setSessionStats(prev => ({
       ...prev,
       cardsStudied: prev.cardsStudied + 1,
       cardsCorrect: prev.cardsCorrect + (status === 'correct' ? 1 : 0),
       cardsIncorrect: prev.cardsIncorrect + (status === 'incorrect' ? 1 : 0),
       cardsToReview: prev.cardsToReview + (status === 'review' ? 1 : 0)
     }))
    
    // Move to next card immediately for better UX
    if (currentCardIndex < totalCards - 1) {
      handleNextCard()
    } else {
      // Complete session immediately, handle Firebase updates in background
      handleCompleteSession()
    }
    
    // Handle Firebase updates in background without blocking UI
    setTimeout(async () => {
      try {
        const promises = []
        
        // Save incorrect and review cards to Firebase using stored card reference
        if (status === 'incorrect') {
          promises.push(flashcardService.saveIncorrectCard(
            cardToProcess.id,
            currentDeck.id,
            currentSession?.id
          ))
        } else if (status === 'review') {
          promises.push(flashcardService.saveReviewCard(
            cardToProcess.id,
            currentDeck.id,
            currentSession?.id
          ))
        }
        
        // Update card progress in Firebase if session exists
        if (currentSession) {
          promises.push(dispatch(updateCardProgress({
            cardId: cardToProcess.id,
            status,
            sessionId: currentSession.id,
            timeSpent: 30 // TODO: Track actual time spent
          }) as any))
        }
        
        // Execute all Firebase operations in parallel
        await Promise.all(promises)
      } catch (error) {
        console.error('Error updating card progress in background:', error)
        // Don't show error to user as UI has already progressed
      }
    }, 0)
  }, [currentCard, currentDeck, currentSession, currentCardIndex, totalCards, dispatch])
  
  const handleCompleteSession = async () => {
    try {
      if (currentSession) {
        // Complete the session in Firebase ve sonucu yönlendirmeye state olarak taşı
        const resultAction = await dispatch(completeStudySession(currentSession.id) as any)
        const sessionResult = (resultAction as any)?.payload as StudySessionResult
        navigate(`/flashcards/session-complete/${currentSession.id}`, { state: { sessionResult } })
      } else {
        // If no session, navigate with session stats as state
        const sessionResult = {
          sessionId: 'local-session',
          cardsStudied: sessionStats.cardsStudied,
          cardsCorrect: sessionStats.cardsCorrect,
          cardsIncorrect: sessionStats.cardsIncorrect,
          duration: Math.floor((new Date().getTime() - sessionStats.startTime.getTime()) / 1000),
          score: sessionStats.cardsStudied > 0 ? Math.round((sessionStats.cardsCorrect / sessionStats.cardsStudied) * 100) : 0,
          xpEarned: sessionStats.cardsCorrect * 10,
          badgesEarned: []
        }
        navigate('/flashcards/session-complete/local', { state: { sessionResult } })
      }
    } catch (error) {
      console.error('Error completing session:', error)
    }
  }
  
  const handleNextCard = () => {
    if (currentCardIndex < totalCards - 1) {
      // First reset the flip state to show question side
      setIsFlipped(false)
      setShowHint(false)
      
      // Delay card change to prevent showing next card's answer during flip animation
      setTimeout(() => {
        setCurrentCardIndex(prev => prev + 1)
      }, 100) // Small delay to ensure flip animation starts properly
    }
  }
  
  const handlePrevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1)
      setIsFlipped(false)
      setShowHint(false)
    }
  }
  
  const handleFlipCard = () => {
    setIsFlipped(!isFlipped)
  }
  
  const handleToggleHint = () => {
    setShowHint(!showHint)
  }
  
  const handleResetCard = () => {
    setIsFlipped(false)
    setShowHint(false)
  }
  
  const getElapsedTime = () => {
    const elapsed = Math.floor((new Date().getTime() - sessionStats.startTime.getTime()) / 1000)
    const minutes = Math.floor(elapsed / 60)
    const seconds = elapsed % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  
  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  if (error || !currentDeck || !currentCard) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {error || 'Deste bulunamadı'}
          </h2>
          <Button onClick={() => navigate('/flashcards')}>
            Flashcard'lara Dön
          </Button>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
            <div className="flex items-center gap-3 md:gap-4 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/flashcards')}
                className="bg-white/80 dark:bg-gray-700/80 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <ArrowLeft size={16} className="mr-1 md:mr-2" />
                <span className="hidden sm:inline">Geri</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleCompleteSession}
                className="bg-orange-500/80 hover:bg-orange-600 dark:bg-orange-600/80 dark:hover:bg-orange-700 text-white border-orange-500 dark:border-orange-600"
              >
                <Check size={16} className="mr-1 md:mr-2" />
                <span className="hidden sm:inline">Bitir</span>
              </Button>
              <div className="flex-1 sm:flex-none">
                <h1 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {currentDeck.title}
                </h1>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                  {currentCardIndex + 1} / {totalCards} kart
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4 w-full sm:w-auto justify-between sm:justify-end">
              <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <Clock size={14} className="md:w-4 md:h-4" />
                  <span className="hidden sm:inline">{getElapsedTime()}</span>
                  <span className="sm:hidden">{getElapsedTime().split(':')[0]}m</span>
                </div>
                <div className="flex items-center gap-1">
                  <Target size={14} className="md:w-4 md:h-4" />
                  <span className="hidden md:inline">{sessionStats.cardsStudied} çalışıldı</span>
                  <span className="md:hidden">{sessionStats.cardsStudied}</span>
                </div>
                <div className="flex items-center gap-1">
                  <BarChart3 size={14} className="md:w-4 md:h-4" />
                  <span>
                    {sessionStats.cardsStudied > 0 
                      ? Math.round((sessionStats.cardsCorrect / sessionStats.cardsStudied) * 100)
                      : 0
                    }%
                  </span>
                </div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
                className="bg-white/80 dark:bg-gray-700/80 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 p-2"
              >
                <Settings size={16} />
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3 md:mt-4">
            <ProgressBar progress={progress} className="h-1.5 md:h-2" />
          </div>
        </div>
      </div>
      
      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 md:py-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Çalışma Ayarları</h3>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Çalışma Modu
                </label>
                <select
                  value={studyMode.type}
                  onChange={(e) => setStudyMode(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="normal">Normal</option>
                  <option value="quick">Hızlı</option>
                  <option value="detailed">Detaylı</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showHints"
                  checked={studyMode.showHints}
                  onChange={(e) => setStudyMode(prev => ({ ...prev, showHints: e.target.checked }))}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                />
                <label htmlFor="showHints" className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                  İpuçlarını göster
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="autoFlip"
                  checked={studyMode.autoFlip}
                  onChange={(e) => setStudyMode(prev => ({ ...prev, autoFlip: e.target.checked }))}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                />
                <label htmlFor="autoFlip" className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                  Otomatik çevirme
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Study Area */}
      <div className="p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Flashcard */}
          <div className="mb-4 sm:mb-6 md:mb-8">
            {currentCard ? (
              <FlashcardComponent
                card={currentCard}
                isFlipped={isFlipped}
                onFlip={handleFlipCard}
                showHint={showHint && studyMode.showHints}
                onToggleHint={handleToggleHint}
              />
            ) : (
              <div className="w-full h-80 sm:h-96 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                <div className="text-center">
                  <div className="text-gray-500 dark:text-gray-400 mb-2 text-sm md:text-base">Kart yükleniyor...</div>
                  <LoadingSpinner size="sm" />
                </div>
              </div>
            )}
          </div>
          
          {/* Controls */}
          <div className="flex flex-col gap-4 md:gap-6">
            {/* Navigation */}
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
              <Button
                variant="outline"
                onClick={handlePrevCard}
                disabled={currentCardIndex === 0}
                className="bg-white/80 dark:bg-gray-700/80 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-xs md:text-sm px-3 md:px-4 py-2"
              >
                <ArrowLeft size={14} className="mr-1 md:mr-2 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Önceki</span>
                <span className="sm:hidden">←</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleResetCard}
                className="bg-white/80 dark:bg-gray-700/80 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-xs md:text-sm px-3 md:px-4 py-2"
              >
                <RotateCcw size={14} className="mr-1 md:mr-2 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Sıfırla</span>
                <span className="sm:hidden">↻</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleFlipCard}
                className="bg-white/80 dark:bg-gray-700/80 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-xs md:text-sm px-3 md:px-4 py-2"
              >
                {isFlipped ? <EyeOff size={14} className="md:w-4 md:h-4" /> : <Eye size={14} className="md:w-4 md:h-4" />}
                <span className="ml-1 md:ml-2 hidden sm:inline">
                  {isFlipped ? 'Soruyu Göster' : 'Cevabı Göster'}
                </span>
                <span className="ml-1 sm:hidden">
                  {isFlipped ? 'Soru' : 'Cevap'}
                </span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleNextCard}
                disabled={currentCardIndex === totalCards - 1}
                className="bg-white/80 dark:bg-gray-700/80 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-xs md:text-sm px-3 md:px-4 py-2"
              >
                <span className="hidden sm:inline">Sonraki</span>
                <span className="sm:hidden">→</span>
                <ArrowRight size={14} className="ml-1 md:ml-2 md:w-4 md:h-4" />
              </Button>
            </div>
            
            {/* Response Buttons - Only show when card is flipped */}
            {isFlipped && (
              <div className="flex flex-row items-center justify-center gap-2 md:gap-4 w-full">
                <Button
                  onClick={() => handleCardResponse('incorrect')}
                  className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white text-sm md:text-base px-4 md:px-6 py-3 md:py-4 w-full sm:flex-1 sm:max-w-[140px] font-medium will-change-transform"
                >
                  <X size={16} className="mr-2 md:w-5 md:h-5" />
                  Bilmiyorum
                </Button>
                
                <Button
                  onClick={() => handleCardResponse('review')}
                  className="bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-800 text-white text-sm md:text-base px-4 md:px-6 py-3 md:py-4 w-full sm:flex-1 sm:max-w-[140px] font-medium will-change-transform"
                >
                  <RefreshCw size={16} className="mr-2 md:w-5 md:h-5" />
                  Tekrar Et
                </Button>
                
                <Button
                  onClick={() => handleCardResponse('correct')}
                  className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white text-sm md:text-base px-4 md:px-6 py-3 md:py-4 w-full sm:flex-1 sm:max-w-[140px] font-medium will-change-transform"
                >
                  <Check size={16} className="mr-2 md:w-5 md:h-5" />
                  Biliyorum
                </Button>
              </div>
            )}
          </div>
          
          {/* Session Stats */}
          <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Card className="p-3 md:p-4 text-center bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm">
              <div className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400 min-h-[1.5rem] flex items-center justify-center">
                {sessionStats.cardsStudied || 0}
              </div>
              <div className="text-sm md:text-sm text-gray-600 dark:text-gray-400 mt-1">Çalışılan</div>
            </Card>
            <Card className="p-3 md:p-4 text-center bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm">
              <div className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400 min-h-[1.5rem] flex items-center justify-center">
                {sessionStats.cardsCorrect || 0}
              </div>
              <div className="text-sm md:text-sm text-gray-600 dark:text-gray-400 mt-1">Doğru</div>
            </Card>
            <Card className="p-3 md:p-4 text-center bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm">
              <div className="text-xl md:text-2xl font-bold text-red-600 dark:text-red-400 min-h-[1.5rem] flex items-center justify-center">
                {sessionStats.cardsIncorrect || 0}
              </div>
              <div className="text-sm md:text-sm text-gray-600 dark:text-gray-400 mt-1">Yanlış</div>
            </Card>
            <Card className="p-3 md:p-4 text-center bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm">
              <div className="text-xl md:text-2xl font-bold text-yellow-600 dark:text-yellow-400 min-h-[1.5rem] flex items-center justify-center">
                {sessionStats.cardsToReview || 0}
              </div>
              <div className="text-sm md:text-sm text-gray-600 dark:text-gray-400 mt-1">Tekrar</div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FlashcardStudy